import {
  HOROSCOPE_DISCLAIMER,
  composeHoroscope,
  computeDayWindows,
  computePanchanga,
  getDefaultAdapter,
  type DayWindows,
  type HoroscopeResult,
  type Panchanga,
} from '@astrally/astro-engine';
import {
  periodKeyFor,
  periodRangeUtc,
  type AstroSystem,
  type AyanamsaId,
  type ChartResult,
  type HoroscopePeriod,
  type LocaleId,
} from '@astrally/shared';

/**
 * The deterministic half of the horoscope surface.
 *
 * HOUSE RULE, restated because this file is where it would be easiest to break:
 * nothing here asks a model anything. Every value rendered on the screen comes
 * out of `@astrally/astro-engine`, computed on-device, and the AI layer only
 * ever rephrases what these functions returned. That is also why this is the
 * INSTANT path — a reading costs zero network calls and zero AI budget, so the
 * screen can be opened fifty times a day for free.
 *
 * Cost note: `composeHoroscope` is 8-50 ms on a desktop for any period, so it
 * runs synchronously during render rather than behind a spinner. The module
 * cache below means flipping between period tabs re-renders from memory.
 */

export type SystemTab = AstroSystem | 'both';

/** Display order for the period control. Mirrors HOROSCOPE_PERIODS. */
export const PERIOD_TABS: HoroscopePeriod[] = ['day', 'week', 'month', 'year'];

export const PERIOD_TAB_LABELS: Record<HoroscopePeriod, string> = {
  day: 'Today',
  week: 'Week',
  month: 'Month',
  year: 'Year',
};

/** What each tradition is called on screen, and the one line that says why it differs. */
export const SYSTEM_LABELS: Record<AstroSystem, { name: string; zodiac: string; anchor: string }> = {
  vedic: {
    name: 'Jyotish',
    zodiac: 'sidereal zodiac — measured from the fixed stars',
    // ReadingCard renders its own JSX copy of this line so "Chandra rashi" can
    // be a tappable <Term>; keep the wording here in sync with it.
    anchor: 'Counted from your Chandra rashi — the rashi your Moon held when you were born.',
  },
  western: {
    name: 'Western',
    zodiac: 'tropical zodiac — measured from the March equinox',
    anchor: "Counted from today's planets meeting your whole birth chart.",
  },
};

export { HOROSCOPE_DISCLAIMER };

// ---------------------------------------------------------------------------
// Where the reader is
// ---------------------------------------------------------------------------

export interface ReaderPlace {
  lat: number;
  lon: number;
  /** IANA zone. Decides what "today" means and where sunrise happens. */
  timezone: string;
  label?: string;
}

/**
 * The location a reading is cast for.
 *
 * Astrally never asks for device location (it is not needed for anything else
 * and the privacy model is "nothing leaves the phone that does not have to"),
 * so the birth place is the one coordinate on file. Using it for BOTH the
 * calendar day and the sunrise keeps the reading internally consistent: the
 * panchanga, the rahu kaal window and the day the key names all describe the
 * same place. `deviceTimezoneDiffers` lets the UI say so when the reader has
 * since moved, instead of quietly showing them another city's morning.
 */
export function readerPlace(chart: ChartResult): ReaderPlace {
  return {
    lat: chart.birth.place.lat,
    lon: chart.birth.place.lon,
    timezone: chart.birth.place.timezone,
    label: chart.birth.place.label,
  };
}

export function deviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * The handle a cached reading and an accuracy rating both refer to.
 * MUST stay byte-identical to `readingKeyFor` in functions/src/horoscope/cache.ts
 * — a rating names this string, and the server matches it against its own copy.
 */
export function readingKeyFor(
  system: AstroSystem,
  period: HoroscopePeriod,
  periodKey: string,
): string {
  return `${system}-${period}-${periodKey}`;
}

// ---------------------------------------------------------------------------
// The natal Moon
// ---------------------------------------------------------------------------

export interface MoonAnchor {
  /** Sidereal sign index, 0 = Aries. */
  sign: number;
  /** False when it came from the midpoint of an uncertain birth-time window. */
  exact: boolean;
}

/**
 * The sidereal sign the Moon occupied at birth — the point the entire Vedic
 * gochara layer is counted from.
 *
 * Deliberately a copy of `natalMoonAnchor` in functions/src/horoscope/
 * horoscopeFns.ts, including the `moonRangeSidereal` shortcut: when an
 * approximate birth window never lets the Moon leave one sign, the sign is
 * still exact even though the time is not.
 */
export function natalMoonAnchor(chart: ChartResult): MoonAnchor | null {
  const exact = chart.positions?.find((p) => p.planet === 'moon');
  if (exact) return { sign: exact.sidereal.sign, exact: true };

  const range = chart.uncertainty?.moonRangeSidereal;
  if (range) {
    const startSign = Math.floor(norm360(range.start) / 30);
    const endSign = Math.floor(norm360(range.end) / 30);
    if (startSign === endSign) return { sign: startSign, exact: true };
  }
  const midpoint = chart.uncertainty?.midpointPositions.find((p) => p.planet === 'moon');
  if (midpoint) return { sign: midpoint.sidereal.sign, exact: false };
  return null;
}

function norm360(deg: number): number {
  const x = deg % 360;
  return x < 0 ? x + 360 : x;
}

// ---------------------------------------------------------------------------
// Building a reading
// ---------------------------------------------------------------------------

export interface ComputedReading {
  system: AstroSystem;
  period: HoroscopePeriod;
  /** '2026-08-07' | '2026-W32' | '2026-08' | '2026' */
  periodKey: string;
  /** `{system}-{period}-{periodKey}` — the id a rating refers to. */
  readingKey: string;
  result: HoroscopeResult;
  /** 'approximate' means the Moon sign came from a window midpoint. */
  moonAnchor: 'exact' | 'approximate';
}

export type ReadingFailure =
  /** No chart on the device yet. */
  | 'no-chart'
  /** An uncertain birth time leaves the natal Moon in more than one sign. */
  | 'moon-sign-uncertain'
  | 'engine-error';

export type ReadingOutcome =
  | { ok: true; reading: ComputedReading }
  | { ok: false; reason: ReadingFailure; message: string };

/**
 * Bounded so a session that flips through every period in both traditions does
 * not hold the ephemeris output of a hundred windows. 4 periods x 2 systems x a
 * few recomputes fits comfortably.
 */
const CACHE_LIMIT = 32;
const readingCache = new Map<string, ComputedReading>();

/** Everything a reading depends on. A changed stamp is a changed reading. */
function chartStamp(chart: ChartResult, ayanamsaId: AyanamsaId, place: ReaderPlace): string {
  return [
    chart.meta.engineVersion,
    chart.meta.calculatedAtISO,
    chart.meta.timeConfidence,
    chart.meta.houseSystem,
    ayanamsaId,
    place.lat.toFixed(4),
    place.lon.toFixed(4),
    place.timezone,
  ].join('|');
}

function remember(key: string, reading: ComputedReading): ComputedReading {
  readingCache.set(key, reading);
  if (readingCache.size > CACHE_LIMIT) {
    // Map iterates in insertion order, so the first key is the oldest.
    const oldest = readingCache.keys().next().value;
    if (oldest !== undefined) readingCache.delete(oldest);
  }
  return reading;
}

/** Test seam — the cache is module state and would otherwise leak between cases. */
export function clearReadingCache(): void {
  readingCache.clear();
}

export function buildReading(args: {
  chart: ChartResult | null;
  system: AstroSystem;
  period: HoroscopePeriod;
  place: ReaderPlace;
  ayanamsaId: AyanamsaId;
  /** Defaults to now. Present so tests are not clock-dependent. */
  at?: Date;
}): ReadingOutcome {
  const { chart, system, period, place, ayanamsaId } = args;
  if (!chart) {
    return {
      ok: false,
      reason: 'no-chart',
      message: 'Your kundli has not been calculated yet.',
    };
  }

  const periodKey = periodKeyFor(period, args.at ?? new Date(), place.timezone);
  const cacheKey = `${chartStamp(chart, ayanamsaId, place)}|${system}|${period}|${periodKey}`;
  const cached = readingCache.get(cacheKey);
  if (cached) return { ok: true, reading: cached };

  const anchor = natalMoonAnchor(chart);
  if (!anchor) {
    return {
      ok: false,
      reason: 'moon-sign-uncertain',
      message: 'Astrally could not place your Moon, so there is nothing honest to read from.',
    };
  }
  /**
   * The Vedic reading REFUSES rather than guesses. Every gochara signal is
   * counted in signs from the natal Moon, so a Moon that could be in either of
   * two signs does not produce a slightly-worse reading — it produces a
   * different one. Western keeps working, because its signals come from
   * transits to the whole natal chart and the Moon sign only colours the
   * supporting text.
   */
  if (system === 'vedic' && !anchor.exact) {
    return {
      ok: false,
      reason: 'moon-sign-uncertain',
      message:
        'Your birth time is uncertain enough that your Moon could be in either of two rashis, and the whole Jyotish reading is counted from that rashi. Astrally will not pick one for you.',
    };
  }

  try {
    const range = periodRangeUtc(periodKey, place.timezone);
    const result = composeHoroscope({
      chart,
      natalMoonSign: anchor.sign,
      period,
      fromUtc: new Date(range.startISO),
      toUtc: new Date(range.endISO),
      system,
      lat: place.lat,
      lon: place.lon,
      ayanamsaId,
    });
    return {
      ok: true,
      reading: remember(cacheKey, {
        system,
        period,
        periodKey,
        readingKey: readingKeyFor(system, period, periodKey),
        result,
        moonAnchor: anchor.exact ? 'exact' : 'approximate',
      }),
    };
  } catch {
    return {
      ok: false,
      reason: 'engine-error',
      message: 'Astrally could not calculate this window. Try another period.',
    };
  }
}

// ---------------------------------------------------------------------------
// The day's panchanga and its windows
// ---------------------------------------------------------------------------

export interface DayView {
  panchanga: Panchanga;
  windows: DayWindows;
}

const dayCache = new Map<string, DayView>();

/**
 * Panchanga + rahu kaal / abhijit for the local day containing `at`. About
 * 10 ms, and cached per local day so re-renders are free.
 */
export function buildDayView(
  place: ReaderPlace,
  ayanamsaId: AyanamsaId,
  at: Date = new Date(),
): DayView | null {
  const dayKey = periodKeyFor('day', at, place.timezone);
  const cacheKey = `${dayKey}|${place.lat.toFixed(4)},${place.lon.toFixed(4)}|${place.timezone}|${ayanamsaId}`;
  const cached = dayCache.get(cacheKey);
  if (cached) return cached;
  try {
    const adapter = getDefaultAdapter();
    const view: DayView = {
      panchanga: computePanchanga(at, place.lat, place.lon, adapter, ayanamsaId, {
        timezone: place.timezone,
      }),
      windows: computeDayWindows(at, place.lat, place.lon, adapter, { timezone: place.timezone }),
    };
    dayCache.set(cacheKey, view);
    if (dayCache.size > 8) {
      const oldest = dayCache.keys().next().value;
      if (oldest !== undefined) dayCache.delete(oldest);
    }
    return view;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** en-IN first: India is the primary market, so "7 August", never "Aug 07". */
const DATE_LOCALES: Record<LocaleId, string> = {
  en: 'en-IN',
  hi: 'hi-IN',
  hinglish: 'en-IN',
  bn: 'bn-IN',
};

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function fmt(key: string, make: () => Intl.DateTimeFormat): Intl.DateTimeFormat {
  let f = fmtCache.get(key);
  if (!f) {
    f = make();
    fmtCache.set(key, f);
  }
  return f;
}

/** 24-hour clock in the reader's zone. `hourCycle` stops midnight rendering as 24:00. */
export function clockIn(timezone: string, at: Date): string {
  return fmt(`t|${timezone}`, () =>
    new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }),
  ).format(at);
}

export function dayMonthIn(timezone: string, locale: LocaleId, at: Date): string {
  return fmt(`dm|${timezone}|${locale}`, () =>
    new Intl.DateTimeFormat(DATE_LOCALES[locale], {
      timeZone: timezone,
      day: 'numeric',
      month: 'long',
    }),
  ).format(at);
}

/**
 * Human label for the window a period key names — the answer to "what exactly
 * am I reading about?", which a bare "Week" tab never gives.
 */
export function periodLabel(
  period: HoroscopePeriod,
  periodKey: string,
  timezone: string,
  locale: LocaleId,
): string {
  let range: { startISO: string; endISO: string };
  try {
    range = periodRangeUtc(periodKey, timezone);
  } catch {
    return periodKey;
  }
  const start = new Date(range.startISO);
  // The range end is EXCLUSIVE — the first instant of the next period. Step
  // back inside the window before naming its last day.
  const last = new Date(new Date(range.endISO).getTime() - 1000);

  switch (period) {
    case 'day':
      return fmt(`d|${timezone}|${locale}`, () =>
        new Intl.DateTimeFormat(DATE_LOCALES[locale], {
          timeZone: timezone,
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        }),
      ).format(start);
    case 'week':
      return `${dayMonthIn(timezone, locale, start)} – ${dayMonthIn(timezone, locale, last)}`;
    case 'month':
      return fmt(`m|${timezone}|${locale}`, () =>
        new Intl.DateTimeFormat(DATE_LOCALES[locale], {
          timeZone: timezone,
          month: 'long',
          year: 'numeric',
        }),
      ).format(start);
    case 'year':
      return periodKey;
  }
}
